import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Filtro estándar del Portal de Cobranza: label chico en mayúsculas + control debajo.
 * Ver /admin/portal-cobranza/inmuebles (AÑO / MES / PROYECTO / DUEÑO). Vive aquí para
 * que cualquier pantalla lo reuse sin volver a copiar la clase del label a mano.
 */
export const FILTER_LABEL_CLS = "text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60";

/**
 * Convención global de "filtro activo": un filtro cuyo valor difiere del base (lo que muestra
 * la pantalla sin filtrar) se marca con `data-activo="true"` y este tono primario, para que se
 * vea de un vistazo qué está recortando la lista. Los controles compartidos de filtro
 * (FilterField, filter-controls, MultiSelectFilter, SearchableSelect) aceptan `activo?: boolean`
 * y lo aplican; un control propio puede usar estas mismas piezas.
 */
/**
 * Borde y fondo del control de filtro en reposo. El borde (`control-border`) da 3:1 contra el
 * fondo de la página (WCAG 1.4.11): con `border-input` era 1.18:1 y el campo casi no se veía.
 */
export const CONTROL_FILTRO_CLS = "border-control-border bg-background";

/** Control activo: borde primario (3.2:1) y texto en primario oscuro (5.1:1, WCAG AA). */
export const FILTRO_ACTIVO_CLS = "border-primary bg-primary/5 text-primary-strong hover:bg-primary/10 hover:text-primary-strong";

/** Texto del valor dentro de un control activo (el control suele pintarlo en gris o foreground). */
export const FILTRO_ACTIVO_TEXTO_CLS = "text-primary-strong";

/** Atributos del control activo: `data-activo="true"` para estilos y pruebas. */
export function atributosFiltroActivo(activo?: boolean): { "data-activo"?: "true" } {
  return activo ? { "data-activo": "true" } : {};
}

/** Label de un filtro. Activo: color primario y un punto delante. */
export function EtiquetaFiltro({ label, activo, className }: { label: string; activo?: boolean; className?: string }) {
  return (
    <span className={cn(FILTER_LABEL_CLS, "inline-flex items-center gap-1.5", activo && "text-primary-strong", className)}>
      {activo && <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-hidden />}
      {label}
      {activo && <span className="sr-only"> (filtro activo)</span>}
    </span>
  );
}

export interface FilterFieldProps {
  label: string;
  children: ReactNode;
  className?: string;
  /** Oculta el texto del label pero conserva su alto, para alinear un botón junto a otros filtros. */
  hideLabel?: boolean;
  /** El valor del filtro difiere del base: label en primario con punto (ver FILTRO_ACTIVO_CLS). */
  activo?: boolean;
}

export function FilterField({ label, children, className, hideLabel, activo }: FilterFieldProps) {
  return (
    <div className={cn("flex flex-col gap-1", className)} {...atributosFiltroActivo(activo)}>
      <EtiquetaFiltro label={label} activo={activo} className={cn(hideLabel && "invisible select-none")} />
      {children}
    </div>
  );
}
